import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import ngAnimate from 'angular-animate';
// 'ui.grid.edit' and 'ui.grid.selection' modules are imported with uiGrid when it runs;
import openDocumentsDialogTemplate from './OpenDocumentsDialog.template.html';

export default angular
  .module('books.dialogs.OpenDocumentsDialog', [
    csmCommonWeb,
    'ui.bootstrap',
    ngAnimate,
    'ui.grid',
    'ui.grid.edit',
    'ui.grid.selection'
  ])
  .service('OpenDocumentsDialog', openDocumentsDialogService).name;

/*****************************************************************************
 * Service: OpenDocumentsDialog
 *
 * Service to display open documents for a given entity with a valid ID number
 *****************************************************************************/
/* @ngInject */
function openDocumentsDialogService(
  $q,
  $timeout,
  $uibModal,
  AddInvoiceDialog,
  BooksSvc,
  BooksSettings,
  BusyScreen,
  uiGridConstants,
  Tools,
  SERVER
) {
  var mSelf = this;

  /***************************************************************************
  * getOpenDocs *
  /**
  * Returns all the open purchase orders and open bills (if includeInvoices is 
  * true) associated with the entityID. 
  * 
  * @param   entityID         Entity ID
  * @param   includeInvoices  Boolean indicating if invoices should be 
  *                           included in the search results.
  ***************************************************************************/
  var getOpenDocs = function (entityID, includeInvoices) {
    var defer = $q.defer();
    BusyScreen.setBusy(true);

    BooksSvc.getOpenPurchaseOrders(entityID).then(function (resp) {
      var POs = resp.data
        .filter(function (item) {
          return !item.isClosed && item.amountOwed > 0;
        })
        .map(function (item) {
          return {
            docID: item.id,
            docDate: item.entryDate,
            docType: 'PO',
            docNumber: item.poNumber,
            docMemo: item.memo,
            docAmount: item.amount,
            docBalance: item.amountOwed
          };
        });

      if (includeInvoices) {
        BooksSvc.getOpenBills(entityID).then(function (resp) {
          var bills = resp.data
            .filter(function (item) {
              return !item.isPaid && item.amount - item.amountPaid > 0;
            })
            .map(function (item) {
              return {
                docID: item.id,
                docDate: item.entryDate,
                docType: 'Invoice',
                docNumber: item.number,
                docMemo: item.memo,
                docAmount: item.amount,
                docBalance: item.amount - item.amountPaid
              };
            });

          /* get open bills and combine with open orders and display on grid. */
          BusyScreen.setBusy(false);
          defer.resolve(POs.concat(bills));
        });
      } else {
        /* Just get POs and close BusyScreen */
        BusyScreen.setBusy(false);
        defer.resolve(POs);
      }
    });

    return defer.promise;
  };

  /***************************************************************************
   * Controller: OpenDocumentsDialogController */
  /**
   * Process the organizations available and provide selection management with
   * the arrow keys.
   *
   * @param orgs
   ***************************************************************************/
  function OpenDocumentsDialogController(
    $scope,
    $uibModalInstance,
    uiGridConstants,
    entity,
    docList,
    includeInvoices,
    restrictVendors,
    vendorsOnly
  ) {
    /*----------------------------------------------*/
    /* Used to hide/show the Create Invoice button. */
    /*----------------------------------------------*/
    $scope.includeInvoices = includeInvoices;

    /*--------------*/
    /* Grid options */
    /*--------------*/
    $scope.resultsGrid = {
      enableColumnMenus: false,
      enableFullRowSelection: true,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.ALWAYS,
      onRegisterApi: function (api) {
        $scope.resultsGrid.api = api;
      },
      columnDefs: [
        {
          field: 'docType',
          displayName: 'Type',
          maxWidth: 80,
          allowCellFocus: false
        },
        {
          field: 'docDate',
          displayName: 'Date',
          maxWidth: 120,
          allowCellFocus: false,
          cellFilter: "date:'MM/dd/yyyy'"
        },
        {
          field: 'docNumber',
          displayName: 'Num',
          maxWidth: 100,
          allowCellFocus: false
        },
        { field: 'docMemo', displayName: 'Title', allowCellFocus: false },
        {
          field: 'docAmount',
          displayName: 'Amount',
          maxWidth: 120,
          allowCellFocus: false,
          cellFilter: 'currency'
        },
        {
          field: 'docBalance',
          displayName: 'Balance',
          maxWidth: 120,
          allowCellFocus: false,
          cellFilter: 'currency'
        }
      ],
      data: docList || []
    };

    $scope.activeEntity = entity;

    /* Entity search based on vendor-only flag passed to directive */
    var searchType = vendorsOnly
      ? SERVER.BooksDBConstants.ET_VENDOR.toString()
      : BooksSettings.ledgerEntityTypes();

    /***************************************************************************
     * createInvoice */
    /**
     * Creates an invoice from the selected POs in the grid. Calls the
     * AddInvoiceDialog, passing it the selected POs and gets an invoice back.
     * The new invoice is push into the grid and selected, along with any
     * previously selected invoices.
     ***************************************************************************/
    $scope.createInvoice = function () {
      var selectedPOs = [];
      var selectedInvoices = [];

      var poids = []; /* Comma separated list of PO IDs */

      /*-------------------------------------------------------*/
      /* Separate selected items into 2 arrays, invoices and   */
      /* POs. Maintain a list of PO ids from the selected POs. */
      /*-------------------------------------------------------*/
      angular.forEach(
        $scope.resultsGrid.api.selection.getSelectedRows(),
        function (element) {
          if (element.docType == 'PO') {
            selectedPOs.push(element);
            poids.push(element.docID);
          } else selectedInvoices.push(element);
        }
      );

      /*-----------------*/
      /* Get PO id items */
      /*-----------------*/
      getPOItems(poids.join(',')).then(function (poItems) {
        var billItems = mSelf.convertPOItemsToBillItems(poItems);

        /* Generate the invoice memo for the AddInvoiceDialog if a single PO is selected */
        var memo =
          selectedPOs.length == 1
            ? 'PO #' + selectedPOs[0].docNumber + ': ' + selectedPOs[0].docMemo
            : '';

        /* Verify entity is provided and is a vendor before sending to AddInvoiceDialog */
        var isVendor =
          $scope.activeEntity &&
          typeof $scope.activeEntity == 'object' &&
          $scope.activeEntity.entity.types.some(function (type) {
            return type == SERVER.BooksDBConstants.ET_VENDOR.toString();
          });

        /* Display Add Invoice dialog with the associated PO items.  */
        AddInvoiceDialog.show(
          isVendor ? $scope.activeEntity : null,
          billItems,
          memo,
          restrictVendors
        ).then(function (data) {
          var total = 0;
          angular.forEach(data.bill.items, function (item) {
            total += item.total || 0;
          });

          var invoice = {
            docID: data.billID,
            docType: 'Invoice',
            docNumber: data.bill.number,
            docMemo: data.bill.memo,
            docDate: data.bill.entryDate,
            docBalance: total,
            docAmount: total
          };

          /*---------------------------------------*/
          /* Add the new invoice document to open  */
          /* docs list and remove the selected POs */
          /*---------------------------------------*/
          $scope.resultsGrid.data.unshift(invoice);

          selectedPOs.forEach(function (doc) {
            $scope.resultsGrid.data.splice(
              $scope.resultsGrid.data.indexOf(doc),
              1
            );
          });

          /*----------------------------------------------------------*/
          /* Trigger the grid to update for the data change and       */
          /* select the new invoice document. The modifyRows needs to */
          /* be called so the internal UIGrid references with the new */
          /* data which is normally done during the next digest cycle */
          /*----------------------------------------------------------*/
          $scope.resultsGrid.api.grid.modifyRows($scope.resultsGrid.data);
          $scope.resultsGrid.api.selection.selectRow(invoice);

          /* scroll to top of grid so the newly added invoice is visible */
          var row = $scope.resultsGrid.data[0];
          var col = $scope.resultsGrid.columnDefs[0];
          $scope.resultsGrid.api.core.scrollTo(row, col);
        });
      });
    };

    /***************************************************************************
     * createInvoiceLabel */
    /**
     * Generates a label for the Create Invoice button based on the current grid
     * selection.
     *
     * @return  String   Button label
     ***************************************************************************/
    $scope.createInvoiceLabel = function () {
      var createNew = 'Create New Invoice';
      var createPO = 'Create New Invoice from PO';
      var createPOs = 'Create New Invoice from POs';

      if (poSelected() == 1) return createPO;
      else if (poSelected() > 1) return createPOs;
      else return createNew;
    };

    /***************************************************************************
     * disablePay */
    /**
     * If no POs have been selected in the grid, returns true. Used to
     * disable the 'Pay Selected' button in the modal dialog.
     *
     * @return  Boolean  True if no rows are selected in the grid.
     ***************************************************************************/
    $scope.disablePay = function () {
      return $scope.resultsGrid.api.selection.getSelectedRows() < 1;
    };

    /***************************************************************************
     * entityLabel */
    /**
     * Maintain a reference to the the addressEntity label function to be used in
     * the lazyTypeahed.
     ***************************************************************************/
    $scope.entityLabel = BooksSettings.entityAddressItemLabel;

    /***************************************************************************
     * getEntities */
    /**
     * Returns a list of entities that match the given text and search types. The
     * search types is a string of comma separated integers representing ledger
     * entity types.
     *
     * @param   text   Search query.
     * @return         Array of sorted corresponding matching entities
     ***************************************************************************/
    $scope.getEntities = function (text) {
      var defer = $q.defer();

      /*---------------------------------------------------------*/
      /* When this dialog is initially opened without an entity, */
      /* prevent a service call and just display the dialog.     */
      /*---------------------------------------------------------*/
      if (!text) return defer.promise;

      BooksSvc.findAddressEntities(text, searchType, true).then(
        function (resp) {
          var entities = resp.data.sort(function (a, b) {
            return a.entity.fullName
              .toLowerCase()
              .localeCompare(b.entity.fullName.toLowerCase());
          });
          defer.resolve(entities);
        }
      );
      return defer.promise;
    };

    /***************************************************************************
* getOpenPOs *
/**
* Called when the Search button is clicked. If an entity has been selected, 
* the corresponding open purchase orders are displayed in the grid otherwise 
* all open purchase orders are displayed. Any grid items selected prior to 
* the search are maintained.   
*
* @return   Promise     Promise to wait on.
***************************************************************************/
    $scope.getOpenPOs = function () {
      var defer = $q.defer();
      var selectedItems = $scope.resultsGrid.api.selection.getSelectedRows();
      var id =
        $scope.activeEntity && $scope.activeEntity.entity
          ? $scope.activeEntity.entity.id
          : null;

      getOpenDocs(id, includeInvoices).then(function (docs) {
        $scope.resultsGrid.data = selectedItems;
        /*----------------------------------------------*/
        /* Avoid duplicates. Do not display items from  */
        /* search results that were previously selected */
        /*----------------------------------------------*/
        angular.forEach(docs, function (doc) {
          if (
            !selectedItems.some(function (item) {
              return doc.docID == item.docID;
            })
          )
            $scope.resultsGrid.data.push(doc);
        });
        defer.resolve(docs);
      });
      return defer.promise;
    };

    /***************************************************************************
* getPOItems *
/**
* Get the list of PO items for the associated ids
*
* @param    ids         Comma separated list of PO ids. 
* @return   Promise     Promise to wait on.
***************************************************************************/
    var getPOItems = function (ids) {
      var defer = $q.defer();

      /* get list of purchase order items for the PO id */
      BooksSvc.getPOItems(ids).then(function (resp) {
        /*---------------------------------------------------*/
        /* Filter out the items with an amount owed of zero. */
        /*---------------------------------------------------*/
        var items = resp.data.filter(function (poi) {
          return !Tools.isNaNZero(poi.amountOwed);
        });

        /*-----------------------------------------------------------*/
        /* Set the "number" and "name" fields to the PO number/memo. */
        /*-----------------------------------------------------------*/
        items.forEach(function (poi) {
          poi.inventoryID = poi.inventoryProductID;
          poi.quantity = poi.quantityRemaining;
          poi.amount = Number(poi.amountOwed);
        });

        defer.resolve(items);
      });

      return defer.promise;
    };

    /***************************************************************************
     * poSelected */
    /**
     * Returns the number of POs selected.
     *
     * @return  Integer  Number of POs selected
     ***************************************************************************/
    var poSelected = function () {
      return $scope.resultsGrid.api.selection
        .getSelectedGridRows()
        .filter(function (element) {
          return element.entity.docType == 'PO';
        }).length;
    };

    /***************************************************************************
     * pay */
    /**
     * Click event handler for the 'Pay Selected' button.
     * Closes the modal dialog and sends back a promise that will resolve with
     * an array of PO and/or bill items.
     ***************************************************************************/
    $scope.pay = function () {
      var count = 0; /* keep count in loop so promise can be resolved */
      var defer = $q.defer();
      var itemList = [];
      var selectedItems = $scope.resultsGrid.api.selection.getSelectedRows();

      /* loop through selected items */
      BusyScreen.setBusy(true);
      angular.forEach(selectedItems, function (value, key) {
        /* item is a purchase order */
        if (value.docType == 'PO') {
          getPOItems(value.docID.toString()).then(function (list) {
            /* set the name and number (used for the memo) */
            angular.forEach(list, function (item) {
              item.name = value.docMemo;
              item.number = 'PO #' + value.docNumber;
            });

            /*---------------------------------------------------*/
            /* Add the items to the list and get the next items. */
            /*---------------------------------------------------*/
            itemList = itemList.concat(list);

            count++;
            if (count == selectedItems.length) {
              defer.resolve(itemList);
              BusyScreen.setBusy(false);
            }
          });
        } else {
          /* item is an invoice */
          /* get list of purchase order items for the PO id */
          BooksSvc.getBillItems(value.docID.toString()).then(function (resp) {
            /*---------------------------------------------------*/
            /* Filter out the items with an amount owed of zero. */
            /*---------------------------------------------------*/
            var items = resp.data.filter(function (bi) {
              return !Tools.isNaNZero(bi.amountOwed);
            });

            /*-------------------------------------------------------------*/
            /* Set the "number" and "name" fields to the Bill number/memo. */
            /*-------------------------------------------------------------*/
            items.forEach(function (bi) {
              bi.itemID = bi.productID;
              bi.number = 'Inv #' + value.docNumber;
              bi.name = value.docMemo;
              bi.balance = Number(value.amount) - Number(value.amountPaid);
            });

            /*---------------------------------------------------*/
            /* Add the items to the list and get the next items. */
            /*---------------------------------------------------*/
            itemList = itemList.concat(items);

            count++;
            if (count == selectedItems.length) {
              defer.resolve(itemList);
              BusyScreen.setBusy(false);
            }
          });
        }
      });
      $uibModalInstance.close(defer.promise);
    };
  }

  /***************************************************************************
   * convertPOItemsToBillItems */
  /**
   * Converts an array of PO items into bill items.
   *
   * @param   items           Array of PO items
   * @return                  Array of bill items
   ***************************************************************************/
  this.convertPOItemsToBillItems = function (items) {
    var billItems = [];
    /*------------------------------*/
    /* convert PO item to bill item */
    /*------------------------------*/
    angular.forEach(items, function (value) {
      var bi = angular.extend({}, value, {
        id: 0,
        poItemID: value.id,
        inventoryID: value.inventoryProductID,
        productID: value.itemID
      });

      /*-----------------------------------------------*/
      /* For partially paid POs, recalculate the       */
      /* price details of billItems to arrive at a     */
      /* total that matches the amount owed on the PO. */
      /*-----------------------------------------------*/
      if (Tools.isNaNZero(bi.quantity) && bi.price > 0) {
        bi.total = bi.amountOwed;
        var taxedSubTotal = bi.total - bi.freight;
        bi.quantity = 1;

        if (bi.taxable)
          bi.subTotal = taxedSubTotal - (bi.taxRate / 100) * taxedSubTotal;
        else bi.subTotal = taxedSubTotal;

        bi.tax = bi.subTotal - taxedSubTotal;
        bi.cost = bi.subTotal / bi.quantity;
      } else bi.total = bi.amountOwed;

      billItems.push(bi);
    });

    return billItems;
  };

  /***************************************************************************
   * show */
  /**
   * Opens the modal window and displays open orders. If there are no open
   * documents and hideWhenEmpty is true, the modal will not be shown.
   *
   * @param   entity           Current entity being worked with.
   * @param   hideWhenEmpty    Boolean indicating if the modal should be shown
   *                           when no open documents exist.
   * @param   includeInvoices  Boolean indicating if invoices should be
   *                           included in the search. If false, the create invoice
   *                           button is not displayed on the modal.
   * @param   vendorsOnly      Boolean indicating if entity search should be
   *                           limited to vendors only.
   * @param   restrictVendors  Boolean indicating if the user should be prevented
   *                           from creating new vendors in the Add Invoice dialog.
   * @return                   Promise to listen on
   ***************************************************************************/
  this.show = function (
    entity,
    hideWhenEmpty,
    includeInvoices,
    vendorsOnly,
    restrictVendors
  ) {
    var openDialog = function (docList) {
      return $uibModal.open({
        animation: true,
        backdrop: 'static',
        resolve: {
          entity: function () {
            return entity;
          },
          docList: function () {
            return docList;
          },
          includeInvoices: function () {
            return includeInvoices;
          },
          vendorsOnly: function () {
            return vendorsOnly;
          },
          restrictVendors: function () {
            return restrictVendors;
          }
        },
        controller: OpenDocumentsDialogController,
        size: 'lg',
        template: openDocumentsDialogTemplate
      }).result;
    };

    /* If entity has a valid ID, retrieve open purchase orders and return a promise with the results */
    if (entity && Number(entity.entity.id)) {
      var defer = $q.defer();

      getOpenDocs(Number(entity.entity.id), includeInvoices).then(
        function (docs) {
          if (hideWhenEmpty && docs.length == 0) defer.reject();
          else openDialog(docs).then(defer.resolve, defer.reject);
        }
      );

      return defer.promise;
    } else return openDialog();
  };
}
